import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Download,
  FileText,
  Sheet,
  BarChart3,
  PieChart,
  TrendingUp,
  Calendar as CalendarIcon,
  Filter,
  Settings,
  Eye,
  RefreshCw,
  Grid3X3,
  Maximize2,
  Edit,
  Move,
  ChevronDown,
  DollarSign,
  Users,
  Clock,
  Target,
  AlertTriangle,
  CheckCircle,
  Activity,
  Briefcase
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  ComposedChart
} from "recharts";

interface ReportingAnalyticsProps {
  onNavigateBack: () => void;
}

interface Widget {
  id: string;
  title: string;
  type: "chart" | "metric" | "table" | "timeline";
  position: { x: number; y: number };
  size: { width: number; height: number };
  data: any;
  customizable: boolean;
}

const budgetUsageData = [
  { month: "Jan", planned: 2000000, actual: 1800000, variance: -200000 },
  { month: "Feb", planned: 2200000, actual: 2100000, variance: -100000 },
  { month: "Mar", planned: 2500000, actual: 2400000, variance: -100000 },
  { month: "Apr", planned: 2800000, actual: 2950000, variance: 150000 },
  { month: "May", planned: 3000000, actual: 2900000, variance: -100000 },
  { month: "Jun", planned: 3200000, actual: 3100000, variance: -100000 }
];

const projectStatusData = [
  { name: "In Progress", value: 12, color: "#3b82f6" },
  { name: "Planning", value: 8, color: "#8b5cf6" },
  { name: "On Hold", value: 3, color: "#f59e0b" },
  { name: "Critical", value: 2, color: "#ef4444" },
  { name: "Completed", value: 15, color: "#10b981" }
];

const timelineData = [
  { month: "Jan", started: 5, completed: 3, delayed: 1 },
  { month: "Feb", started: 7, completed: 4, delayed: 2 },
  { month: "Mar", started: 6, completed: 6, delayed: 1 },
  { month: "Apr", started: 8, completed: 5, delayed: 3 },
  { month: "May", started: 4, completed: 7, delayed: 1 },
  { month: "Jun", started: 6, completed: 8, delayed: 2 }
];

const kpiData = [
  { 
    metric: "Budget Utilization", 
    current: 87, 
    target: 85, 
    trend: "up", 
    change: "+2%",
    status: "good" 
  },
  { 
    metric: "On-Time Delivery", 
    current: 73, 
    target: 80, 
    trend: "down", 
    change: "-5%",
    status: "warning" 
  },
  { 
    metric: "Resource Efficiency", 
    current: 92, 
    target: 90, 
    trend: "up", 
    change: "+3%",
    status: "good" 
  },
  { 
    metric: "Stakeholder Satisfaction", 
    current: 88, 
    target: 85, 
    trend: "up", 
    change: "+1%",
    status: "good" 
  },
  { 
    metric: "Risk Mitigation", 
    current: 65, 
    target: 75, 
    trend: "down", 
    change: "-8%",
    status: "critical" 
  }
];

const departmentBudgetData = [
  { department: "IT", allocated: 5000000, spent: 4200000, remaining: 800000 },
  { department: "Marketing", allocated: 2500000, spent: 2300000, remaining: 200000 },
  { department: "Operations", allocated: 3500000, spent: 3100000, remaining: 400000 },
  { department: "HR", allocated: 1500000, spent: 1200000, remaining: 300000 },
  { department: "Finance", allocated: 2000000, spent: 1800000, remaining: 200000 }
];

export function ReportingAnalytics({ onNavigateBack }: ReportingAnalyticsProps) {
  const [selectedDateRange, setSelectedDateRange] = useState("last-6-months");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedWidget, setSelectedWidget] = useState<string | null>(null);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [detailViewData, setDetailViewData] = useState<any>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);

  const handleExportPDF = () => {
    toast.success("PDF Export", {
      description: "Your analytics report is being generated and will be downloaded shortly."
    });
  };

  const handleExportExcel = () => {
    toast.success("Excel Export", {
      description: "Your data export is being prepared and will be downloaded shortly."
    });
  };

  const handleWidgetClick = (widgetType: string, data: any) => {
    setDetailViewData({ type: widgetType, data });
    setIsDetailViewOpen(true);
  };

  const handleRefreshData = () => {
    toast.info("Refreshing Data", {
      description: "Updating all analytics with the latest information..."
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "good": return "text-green-600";
      case "warning": return "text-yellow-600";
      case "critical": return "text-red-600";
      default: return "text-gray-600";
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={onNavigateBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl">Reporting & Analytics</h1>
              <p className="text-muted-foreground">
                Comprehensive insights and data visualization for informed decision making
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefreshData} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            
            <Button
              variant={isCustomizing ? "default" : "outline"}
              onClick={() => setIsCustomizing(!isCustomizing)}
              className="flex items-center gap-2"
            >
              <Grid3X3 className="h-4 w-4" />
              {isCustomizing ? "Done" : "Customize"}
            </Button>
            
            <Separator orientation="vertical" className="h-8" />
            
            <Button onClick={handleExportPDF} className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Export PDF
            </Button>
            
            <Button variant="outline" onClick={handleExportExcel} className="flex items-center gap-2">
              <Sheet className="h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Filters and Controls */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedDateRange} onValueChange={setSelectedDateRange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last-30-days">Last 30 Days</SelectItem>
                    <SelectItem value="last-3-months">Last 3 Months</SelectItem>
                    <SelectItem value="last-6-months">Last 6 Months</SelectItem>
                    <SelectItem value="last-year">Last Year</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    <SelectItem value="it">IT</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="hr">HR</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Badge variant="outline" className="ml-auto">
                Data as of {new Date().toLocaleDateString()}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Analytics Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="budget">Budget Analysis</TabsTrigger>
            <TabsTrigger value="projects">Project Analytics</TabsTrigger>
            <TabsTrigger value="timeline">Timeline & Delivery</TabsTrigger>
            <TabsTrigger value="kpis">KPI Dashboard</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className={isCustomizing ? "border-dashed border-2 border-blue-300" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Total Budget
                    </CardTitle>
                    {isCustomizing && (
                      <Button variant="ghost" size="sm">
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">$14.7M</div>
                  <p className="text-xs text-muted-foreground">+12% from last period</p>
                </CardContent>
              </Card>

              <Card className={isCustomizing ? "border-dashed border-2 border-blue-300" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Active Projects
                    </CardTitle>
                    {isCustomizing && (
                      <Button variant="ghost" size="sm">
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">40</div>
                  <p className="text-xs text-muted-foreground">5 started this month</p>
                </CardContent>
              </Card>

              <Card className={isCustomizing ? "border-dashed border-2 border-blue-300" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Completion Rate
                    </CardTitle>
                    {isCustomizing && (
                      <Button variant="ghost" size="sm">
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">73%</div>
                  <p className="text-xs text-muted-foreground">-5% from target</p>
                </CardContent>
              </Card>

              <Card className={isCustomizing ? "border-dashed border-2 border-blue-300" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      At-Risk Projects
                    </CardTitle>
                    {isCustomizing && (
                      <Button variant="ghost" size="sm">
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">5</div>
                  <p className="text-xs text-muted-foreground">Requires attention</p>
                </CardContent>
              </Card>
            </div>

            {/* Main Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Budget Usage Chart */}
              <Card className={isCustomizing ? "border-dashed border-2 border-blue-300" : ""}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Budget Usage Trend
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {isCustomizing && (
                        <Button variant="ghost" size="sm">
                          <Edit className="h-3 w-3" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleWidgetClick("budget-usage", budgetUsageData)}
                      >
                        <Maximize2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={budgetUsageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                      <Bar dataKey="planned" fill="#e2e8f0" name="Planned" />
                      <Bar dataKey="actual" fill="#3b82f6" name="Actual" />
                      <Line type="monotone" dataKey="variance" stroke="#ef4444" name="Variance" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Project Status Distribution */}
              <Card className={isCustomizing ? "border-dashed border-2 border-blue-300" : ""}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Project Status Distribution
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {isCustomizing && (
                        <Button variant="ghost" size="sm">
                          <Edit className="h-3 w-3" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleWidgetClick("project-status", projectStatusData)}
                      >
                        <Maximize2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        dataKey="value"
                        data={projectStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                        outerRadius={80}
                        fill="#8884d8"
                      >
                        {projectStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Budget Analysis Tab */}
          <TabsContent value="budget" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Department Budget Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Department Budget Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={departmentBudgetData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                      <Bar dataKey="allocated" fill="#e2e8f0" name="Allocated" />
                      <Bar dataKey="spent" fill="#3b82f6" name="Spent" />
                      <Bar dataKey="remaining" fill="#10b981" name="Remaining" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Budget Utilization Over Time */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Budget Utilization Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={budgetUsageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Area type="monotone" dataKey="actual" stackId="1" stroke="#3b82f6" fill="#3b82f6" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Project Analytics Tab */}
          <TabsContent value="projects" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Project Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Project Timeline Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={timelineData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="started" fill="#3b82f6" name="Started" />
                      <Bar dataKey="completed" fill="#10b981" name="Completed" />
                      <Bar dataKey="delayed" fill="#ef4444" name="Delayed" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Project Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Project Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { name: "Average Project Duration", value: "186 days", trend: "down", change: "-12 days" },
                      { name: "Budget Variance", value: "8.3%", trend: "up", change: "+2.1%" },
                      { name: "Success Rate", value: "87%", trend: "up", change: "+5%" },
                      { name: "Resource Utilization", value: "92%", trend: "up", change: "+3%" }
                    ].map((metric, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{metric.name}</p>
                          <p className="text-2xl font-bold">{metric.value}</p>
                        </div>
                        <div className={`text-sm ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                          {metric.change}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Timeline & Delivery Tab */}
          <TabsContent value="timeline" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Delivery Performance Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="started" stroke="#3b82f6" strokeWidth={2} name="Projects Started" />
                    <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Projects Completed" />
                    <Line type="monotone" dataKey="delayed" stroke="#ef4444" strokeWidth={2} name="Projects Delayed" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KPI Dashboard Tab */}
          <TabsContent value="kpis" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {kpiData.map((kpi, index) => (
                <Card key={index} className="cursor-pointer hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-sm">{kpi.metric}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-3xl font-bold">{kpi.current}%</span>
                        <Badge variant={kpi.status === 'good' ? 'default' : kpi.status === 'warning' ? 'secondary' : 'destructive'}>
                          {kpi.change}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Target: {kpi.target}%</span>
                          <span className={getStatusColor(kpi.status)}>
                            {kpi.status.charAt(0).toUpperCase() + kpi.status.slice(1)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              kpi.status === 'good' ? 'bg-green-600' : 
                              kpi.status === 'warning' ? 'bg-yellow-600' : 'bg-red-600'
                            }`}
                            style={{ width: `${Math.min(kpi.current, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Detail View Dialog */}
        <Dialog open={isDetailViewOpen} onOpenChange={setIsDetailViewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {detailViewData?.type === 'budget-usage' && 'Detailed Budget Usage Analysis'}
                {detailViewData?.type === 'project-status' && 'Project Status Details'}
              </DialogTitle>
              <DialogDescription>
                {detailViewData?.type === 'budget-usage' && 'Comprehensive breakdown of budget planning vs actual spending with variance analysis.'}
                {detailViewData?.type === 'project-status' && 'Detailed view of project distribution across different status categories.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {detailViewData?.type === 'budget-usage' && (
                <div>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={detailViewData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                      <Bar dataKey="planned" fill="#e2e8f0" name="Planned Budget" />
                      <Bar dataKey="actual" fill="#3b82f6" name="Actual Spending" />
                      <Line type="monotone" dataKey="variance" stroke="#ef4444" name="Budget Variance" />
                    </ComposedChart>
                  </ResponsiveContainer>
                  
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Budget Analysis Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Planned</p>
                        <p className="text-xl font-bold">{formatCurrency(15700000)}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Actual</p>
                        <p className="text-xl font-bold">{formatCurrency(15200000)}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Variance</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(500000)} Under</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {detailViewData?.type === 'project-status' && (
                <div>
                  <ResponsiveContainer width="100%" height={400}>
                    <RechartsPieChart>
                      <Pie
                        dataKey="value"
                        data={detailViewData.data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => `${name}: ${value} (${percent ? (percent * 100).toFixed(0) : 0}%)`}
                        outerRadius={120}
                        fill="#8884d8"
                      >
                        {detailViewData.data.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                  
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Project Status Breakdown</h4>
                    <div className="space-y-3">
                      {detailViewData.data.map((status: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: status.color }}
                            ></div>
                            <span className="font-medium">{status.name}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{status.value} projects</div>
                            <div className="text-sm text-muted-foreground">
                              {((status.value / 40) * 100).toFixed(0)}% of total
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}